// @ts-nocheck

import { supabase, withConnectionsAccess } from '@/lib/supabaseGuard';
import { classifySupabaseError, withErrorHandling, logError } from '@/utils/errorHandling';
import { mapCanonicalCreateRequestResult } from '@/services/friends/mapCanonicalCreateRequestResult';

// Define types for better type safety
export interface FriendRequest {
  id: string;
  sender_id: string;
  recipient_id: string;
  status: string;
  created_at: string;
  declined_at?: string;
  requester: {
    id: string;
    name: string;
    email?: string;
    photo_urls?: string[];
  };
}

export interface AcceptFriendRequestResult {
  success: boolean;
  chatId?: string;
  error?: string;
}

export const sendFriendRequest = async (senderId: string, receiverId: string) => {
  console.log('🚀 sendFriendRequest:', { senderId, receiverId });

  // V175.BLOCK: Check connections table for BLOCKED or ACCEPTED status
  const { data: existingConnections, error: connectionCheckError } = await withConnectionsAccess(() =>
    supabase
      .from('connections')
      .select('id, status')
      .or(`and(user_id.eq.${senderId},connection_user_id.eq.${receiverId}),and(user_id.eq.${receiverId},connection_user_id.eq.${senderId})`)
      .in('status', ['ACCEPTED', 'BLOCKED'])
      .limit(1)
  );

  if (connectionCheckError) {
    console.warn('⚠️ Error checking existing connections (non-critical):', connectionCheckError);
  } else if (existingConnections && existingConnections.length > 0) {
    const connection = existingConnections[0];

    // V175.BLOCK: Check if connection is blocked
    if (connection.status === 'BLOCKED') {
      console.log('🚫 V175.BLOCK: Cannot send friend request - connection is blocked');
      return {
        data: null,
        error: {
          message: 'Cannot send friend request to this user',
          code: 'USER_BLOCKED'
        } as any
      };
    }

    // Already friends
    console.log('⚠️ Users are already connected in connections table');
    return {
      data: null,
      error: {
        message: 'You are already friends with this user',
        code: 'ALREADY_FRIENDS'
      } as any
    };
  }

  // Route through canonical RPC to prevent directional duplicate races.
  const inviteCode = `${senderId.substring(0, 8)}-${Date.now().toString(36)}`;
  const idempotencyKey = `create-request:${senderId}:${receiverId}`;
  const { data: rpcData, error: rpcError } = await supabase.rpc('create_connection_request_canonical', {
    p_sender_id: senderId,
    p_recipient_id: receiverId,
    p_invite_code: inviteCode,
    p_idempotency_key: idempotencyKey,
  });

  if (rpcError) {
    console.error('❌ Error creating canonical friend request:', rpcError);
    return { data: null, error: rpcError };
  }

  const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
  return mapCanonicalCreateRequestResult(row, { senderId, receiverId });
};

// 🧹 CLEAN: Create fresh connection request for testing
export const createTestConnectionRequest = async (targetUserId: string) => {
  try {
    if (!import.meta.env.DEV) {
      console.warn('createTestConnectionRequest is disabled outside development');
      return null;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const inviteCode = user.id.substring(0, 12);

    const { data, error } = await supabase.rpc('create_connection_request_canonical', {
      p_sender_id: user.id,
      p_recipient_id: targetUserId,
      p_invite_code: inviteCode,
      p_idempotency_key: `test-create-request:${user.id}:${targetUserId}`,
    });

    if (error) {
      console.error('Failed to create test request:', error);
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row?.success) return null;

    return `https://blissnet.app/invite/${inviteCode}`;

  } catch (err) {
    console.error('Error creating test request:', err);
    return null;
  }
};

export const acceptFriendRequest = async (
  requestId: string,
  senderId: string,
  senderName: string
): Promise<AcceptFriendRequestResult> => {

  console.log('🚀 V167.3: Accepting friend request');
  console.log('🚀 V167.3: Request ID:', requestId);
  console.log('🚀 V167.3: Other user ID:', senderId);

  try {
    // Get current authenticated user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { success: false, error: 'Authentication required' };
    }

    const recipientId = user.id;
    if (recipientId === senderId) {
      return { success: false, error: 'Cannot accept connection request from yourself' };
    }

    const idempotencyKey = `approve:${requestId}:${recipientId}`;
    const { data: rpcData, error: rpcError } = await supabase.rpc('approve_connection_request_canonical', {
      p_request_id: requestId,
      p_approver_id: recipientId,
      p_idempotency_key: idempotencyKey,
    });

    if (rpcError) {
      return {
        success: false,
        error: `Connection approval failed: ${rpcError.message}`
      };
    }

    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    if (!row?.success) {
      return {
        success: false,
        error: row?.error || 'Failed to approve request'
      };
    }

    // CURSOR PATCH: Server RPC recomputes both users (no 403 / client updateUserTrustScore)
    try {
      await supabase.rpc('recompute_trust_for_users', { user_ids: [senderId, recipientId] });
    } catch (e) {
      console.warn('[TrustScore] recompute RPC failed', e);
    }

    // V320.RELOAD.3: Prove whether user_events rows were inserted (diagnose push chain)
    const { data: dbgEvents, error: dbgErr } = await supabase
      .from('user_events')
      .select('id,event_type,user_id,created_at,payload')
      .order('created_at', { ascending: false })
      .limit(5);
    console.log('[V320.USER_EVENTS][DBG] latest', { dbgErr, dbgEvents });

    return {
      success: true,
      chatId: row.connection_id
    };

  } catch (error: any) {
    console.error('Friend request acceptance failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to create connection'
    };
  }
};

export const calculateUserTrustScore = async (userId: string): Promise<number> => {
  try {
    const { data: connections, error } = await supabase
      .from('connections')
      .select('id')
      .or(`user_id.eq.${userId},connection_user_id.eq.${userId}`)
      .eq('status', 'ACCEPTED'); // Use uppercase to match enum

    if (error) {
      console.error('Trust score query error:', error);
      return 0;
    }

    const friendCount = connections?.length || 0;
    const trustScore = friendCount * 10; // 10 points per friend

    return trustScore;

  } catch (err) {
    console.error('Trust score calculation error:', err);
    return 0;
  }
};

export const getUserConnections = async (userId: string) => {
  try {
    console.log('🚀 V161.18: friendRequestService using connection coordinator');
    // V161.18: Use connection coordinator instead of direct query
    const { getBasicConnections } = await import('../../utils/connectionCoordinator');
    const connections = await getBasicConnections(userId);

    // Convert to expected format
    return connections
      .filter(conn => conn.status === 'ACCEPTED' && conn.user_id && conn.connection_user_id)
      .map(conn => ({
        connectionId: conn.id,
        friendId: conn.user_id === userId ? conn.connection_user_id : conn.user_id,
        type: conn.type,
        degree: conn.degree || 1,
        connectedAt: conn.created_at
      }));
  } catch (error) {
    console.error('❌ V161.20: getUserConnections coordinator failed:', error);
    // V161.20: NO FALLBACK - coordinator is the only source
    return [];

  }
};

export const testConnectionSystem = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return;
    }

    // V163.4: Use NetworkDataContext instead of direct queries
    console.warn('⚠️ V163.4: testNetworkIntegrity() - Use NetworkDataContext for connection requests');

    // Test 1: Connection requests now loaded via NetworkDataContext
    // Components should use useConnectionRequests() hook instead
    const pendingRequests = []; // Neutralized - use context hook

    // Test 2: Check connections (main data)
    const connections = await getUserConnections(user.id);
    const trustScore = await calculateUserTrustScore(user.id);

    // Test 3: Accepted requests now handled by NetworkDataContext
    const acceptedRequests = []; // Neutralized - use context hook

    if (process.env.NODE_ENV === 'development') {
      // Test 4: RLS policies
      const { data: testQuery, error: testError } = await supabase
        .from('connections')
        .select('count')
        .or(`user_id.eq.${user.id},connection_user_id.eq.${user.id}`)
        .limit(1);
    }

  } catch (err) {
    // Test failed
  }
};

// Remove the old handleSupabaseError function as it's now in errorHandling.ts

/**
 * V167.DISCONNECT - Simplified disconnect method
 * Tries multiple approaches to find connection (brute force debugging)
 * Uses withConnectionsAccess to respect supabaseGuard restrictions
 */
const fallbackDisconnectMethod = async (userId: string, otherUserId: string): Promise<{ success: boolean; error?: string }> => {
  console.log('🔍 V167.DISCONNECT: Simple version starting');
  console.log('  - userId:', userId);
  console.log('  - otherUserId:', otherUserId);
  
  try {
    // Method 1: Try direct match (user_id → connection_user_id) with ACCEPTED status
    console.log('🔍 V167.DISCONNECT: Trying direction 1 (user_id → connection_user_id, ACCEPTED)...');
    const { data: conn1, error: err1 } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('*')
        .eq('user_id', userId)
        .eq('connection_user_id', otherUserId)
        .eq('status', 'ACCEPTED')
        .maybeSingle()
    );
    
    if (err1) {
      console.error('❌ V167.DISCONNECT: Direction 1 error:', err1);
    }
    console.log('🔍 V167.DISCONNECT: Direction 1 result:', conn1);
    
    // Method 2: Try reverse direction (connection_user_id → user_id) with ACCEPTED status
    console.log('🔍 V167.DISCONNECT: Trying direction 2 (connection_user_id → user_id, ACCEPTED)...');
    const { data: conn2, error: err2 } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('*')
        .eq('user_id', otherUserId)
        .eq('connection_user_id', userId)
        .eq('status', 'ACCEPTED')
        .maybeSingle()
    );
    
    if (err2) {
      console.error('❌ V167.DISCONNECT: Direction 2 error:', err2);
    }
    console.log('🔍 V167.DISCONNECT: Direction 2 result:', conn2);
    
    // Method 3: Try ANY connection between these users (no status filter) - direction 1
    console.log('🔍 V167.DISCONNECT: Trying without status filter (direction 1)...');
    const { data: anyConn1, error: anyErr1 } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('*')
        .eq('user_id', userId)
        .eq('connection_user_id', otherUserId)
        .maybeSingle()
    );
    
    if (anyErr1) {
      console.error('❌ V167.DISCONNECT: Any status (direction 1) error:', anyErr1);
    }
    console.log('🔍 V167.DISCONNECT: Any status (direction 1):', anyConn1);
    
    // Method 4: Try ANY connection between these users (no status filter) - direction 2
    console.log('🔍 V167.DISCONNECT: Trying without status filter (direction 2)...');
    const { data: anyConn2, error: anyErr2 } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('*')
        .eq('user_id', otherUserId)
        .eq('connection_user_id', userId)
        .maybeSingle()
    );
    
    if (anyErr2) {
      console.error('❌ V167.DISCONNECT: Any status (direction 2) error:', anyErr2);
    }
    console.log('🔍 V167.DISCONNECT: Any status (direction 2):', anyConn2);
    
    // Collect all found connections
    const foundConnections = [conn1, conn2, anyConn1, anyConn2].filter(Boolean);
    
    console.log('🔍 V167.DISCONNECT: Total connections found:', foundConnections.length);
    
    if (foundConnections.length === 0) {
      console.error('❌ V167.DISCONNECT: NO CONNECTIONS FOUND AT ALL');
      console.error('❌ This means the user IDs are wrong or connection does not exist');
      
      // Debug: Show what connections DO exist for this user
      console.log('🔍 V167.DISCONNECT: Fetching ALL connections for user to debug...');
      const { data: allUserConns, error: allErr } = await withConnectionsAccess(() =>
        supabase
          .from('connections')
          .select('id, user_id, connection_user_id, status, type')
          .eq('user_id', userId)
      );
      
      if (allErr) {
        console.error('❌ V167.DISCONNECT: Error fetching all connections:', allErr);
      }
      
      console.log('🔍 V167.DISCONNECT: All connections for user:', allUserConns);
      console.log('🔍 V167.DISCONNECT: Total user connections:', allUserConns?.length || 0);
      
      if (allUserConns && allUserConns.length > 0) {
        console.log('🔍 V167.DISCONNECT: Connection details:');
        allUserConns.forEach((conn, index) => {
          console.log(`  ${index + 1}. ID: ${conn.id}`);
          console.log(`     user_id: ${conn.user_id}`);
          console.log(`     connection_user_id: ${conn.connection_user_id}`);
          console.log(`     status: ${conn.status}`);
          console.log(`     type: ${conn.type}`);
          console.log(`     Match with otherUserId? ${conn.connection_user_id === otherUserId}`);
          console.log(`     Exact match: "${conn.connection_user_id}" === "${otherUserId}" = ${conn.connection_user_id === otherUserId}`);
        });
      }
      
      return { success: false, error: 'No connection found between users' };
    }
    
    // Use the first found connection
    const connectionToUpdate = foundConnections[0];
    console.log('✅ V167.DISCONNECT: Found connection:', connectionToUpdate.id);
    console.log('  - Status:', connectionToUpdate.status);
    console.log('  - Direction:', connectionToUpdate.user_id === userId ? 'outgoing' : 'incoming');
    
    // Update status to DISCONNECTED
    console.log('🔍 V167.DISCONNECT: Updating status to DISCONNECTED...');
    const { error: updateError } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .update({ 
          status: 'DISCONNECTED',
          updated_at: new Date().toISOString()
        })
        .eq('id', connectionToUpdate.id)
    );
    
    if (updateError) {
      console.error('❌ V167.DISCONNECT: Update error:', updateError);
      return { success: false, error: updateError.message };
    }
    
    console.log('✅ V167.DISCONNECT: Successfully disconnected!');
    
    // CURSOR PATCH: Server RPC recomputes both users after disconnect
    try {
      await supabase.rpc('recompute_trust_for_users', { user_ids: [userId, otherUserId] });
    } catch (e) {
      console.warn('[TrustScore] recompute RPC failed', e);
    }
    
    console.log('🔍 V167.DISCONNECT: ========== END (SUCCESS) ==========');
    return { success: true };
  } catch (error) {
    console.error('❌ V167.DISCONNECT: Exception in disconnect:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Disconnect operation failed' 
    };
  }
};

/**
 * Retry disconnect with enhanced headers for 406/JSON format errors
 */
const retryDisconnectWithHeaders = async (userId: string, targetUserId: string): Promise<{ success: boolean; error?: string }> => {
  try {

    // Create a new supabase client instance with enhanced headers
    const { createClient } = await import('@supabase/supabase-js');
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const retryClient = createClient(supabaseUrl, supabaseKey, {
      global: {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Client-Info': 'blissnet-disconnect-retry',
          'User-Agent': 'BlissNet/1.0 (Disconnect-Retry)'
        }
      }
    });

    const { data, error } = await retryClient.rpc('disconnect_users_transaction', {
      p_user_id: userId,
      p_connection_user_id: targetUserId
    });

    if (error) {
      console.error('❌ RETRY: Enhanced headers retry failed:', error);
      return { success: false, error: 'Retry with enhanced headers failed' };
    }

    if (data && data.success) {
      return { success: true };
    }

    return { success: false, error: data?.error || 'Retry operation failed' };

  } catch (error) {
    console.error('❌ RETRY: Exception in enhanced headers retry:', error);
    return { success: false, error: 'Enhanced headers retry exception' };
  }
};

/**
 * V168.DISCONNECT - Super simple disconnect by connection ID
 * Just update the connection status directly - no searching needed!
 * @param connectionId - The connection table ID (primary key)
 * @param skipTrustScoreUpdate - Skip trust score update (e.g., when blocking)
 * @returns Promise with success status and error message if any
 */
export const disconnectConnection = async (
  connectionId: string,
  skipTrustScoreUpdate: boolean = false
): Promise<{ success: boolean; error?: string }> => {
  console.log('🔍 DISCONNECT: Updating connection:', connectionId);

  try {
    // Step 1: Get the connection to find both user IDs (needed for chat cleanup)
    const { data: conn } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('user_id, connection_user_id')
        .eq('id', connectionId)
        .single()
    );

    if (!conn) {
      console.error('❌ DISCONNECT: Connection not found:', connectionId);
      return { success: false, error: 'Connection not found' };
    }

    // Step 2: Update connection status to DISCONNECTED
    const { error } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .update({ 
          status: 'DISCONNECTED',
          updated_at: new Date().toISOString()
        })
        .eq('id', connectionId)
    );

    if (error) {
      console.error('❌ DISCONNECT: Error updating connection:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ DISCONNECT: Connection status updated to DISCONNECTED');

    // Step 3: Handle chats - Set connection_id to NULL to preserve chat history
    // This allows reconnection to restore the chat by updating connection_id to the new connection
    try {
      console.log('🔍 DISCONNECT: Updating chats to remove connection_id reference');
      
      // Find all chats referencing this connection
      const { data: chats, error: chatQueryError } = await supabase
        .from('chats')
        .select('id')
        .eq('connection_id', connectionId)
        .eq('chat_type', 'friend');

      if (chatQueryError) {
        console.warn('⚠️ DISCONNECT: Error querying chats (non-critical):', chatQueryError);
      } else if (chats && chats.length > 0) {
        console.log(`🔍 DISCONNECT: Found ${chats.length} chat(s) to update`);
        
        // Set connection_id to NULL for all chats referencing this connection
        const { error: chatUpdateError } = await supabase
          .from('chats')
          .update({ connection_id: null })
          .eq('connection_id', connectionId)
          .eq('chat_type', 'friend');

        if (chatUpdateError) {
          console.warn('⚠️ DISCONNECT: Failed to update chats (non-critical):', chatUpdateError);
          // Don't fail the disconnect operation if chat update fails
        } else {
          console.log('✅ DISCONNECT: Chat connection_id set to NULL (chat history preserved)');
          
          // Clear chat cache for both users
          try {
            const { ChatMessageCache } = await import('../ChatMessageCache');
            chats.forEach(chat => {
              ChatMessageCache.clearChat(chat.id, conn.user_id);
              ChatMessageCache.clearChat(chat.id, conn.connection_user_id);
            });
            console.log('✅ DISCONNECT: Chat cache cleared');
          } catch (cacheError) {
            console.warn('⚠️ DISCONNECT: Failed to clear chat cache (non-critical):', cacheError);
          }
        }
      } else {
        console.log('ℹ️ DISCONNECT: No chats found for this connection');
      }
    } catch (chatError) {
      console.warn('⚠️ DISCONNECT: Failed to handle chats (non-critical):', chatError);
      // Don't fail the disconnect operation if chat handling fails
    }
    
    // Step 4: Server RPC recomputes both users (unless skipped for block action)
    if (!skipTrustScoreUpdate) {
      try {
        await supabase.rpc('recompute_trust_for_users', { user_ids: [conn.user_id, conn.connection_user_id] });
      } catch (e) {
        console.warn('[TrustScore] recompute RPC failed', e);
      }
    } else {
      console.log('ℹ️ DISCONNECT: Skipping trust score update (block action)');
    }
    
    return { success: true };

  } catch (error: any) {
    console.error('❌ DISCONNECT: Exception:', error);
    return { success: false, error: error.message };
  }
};

/**
 * V167.DISCONNECT - Simplified disconnect service (legacy - use disconnectConnection instead)
 * Finds connection by user IDs and deletes it (no connectionRecordId needed)
 * @param userId - The ID of the user initiating the disconnect
 * @param otherUserId - The ID of the user to disconnect from
 * @returns Promise with success status and error message if any
 */
export const disconnectUser = async (userId: string, otherUserId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // ✅ SECURITY: Prevent self-disconnection
    if (userId === otherUserId) {
      console.error('❌ V167.DISCONNECT: Cannot disconnect from self');
      return { success: false, error: 'Cannot disconnect from yourself' };
    }

    // 🎯 SIMPLE METHOD: Find by user IDs and delete
    return await fallbackDisconnectMethod(userId, otherUserId);

  } catch (err) {
    console.error('❌ V167.DISCONNECT: Service layer error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Disconnect operation failed'
    };
  }
};

export const debugConnections = async () => {
  if (process.env.NODE_ENV !== 'development') return;

  try {
    // V163.6: Use NetworkDataContext instead of direct connections query
    console.warn('⚠️ V163.6: debugConnections() - Use NetworkDataContext for connections');
    const allConnections = []; // Neutralized - use context hook
    const connectionsError = null;

    // V163.4: Connection requests now loaded via NetworkDataContext
    console.warn('⚠️ V163.4: debugConnections() - Use NetworkDataContext for connection requests');
    const allRequests = []; // Neutralized - use useConnectionRequests() hook
    const requestsError = null;

    if (connectionsError) {
      // Error fetching connections
    }
    if (requestsError) {
      // Error fetching requests
    }

    // Show clean state summary
    const pendingRequests = allRequests?.filter(r => r.status === 'pending') || [];
    const acceptedRequests = allRequests?.filter(r => r.status === 'accepted') || [];

  } catch (err) {
    console.error('💥 Debug failed:', err);
  }
};

export const declineFriendRequest = async (requestId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('connection_requests')
      .update({
        status: 'declined',
        declined_at: new Date().toISOString()
      })
      .eq('id', requestId);

    if (error) {
      console.error('Error declining friend request:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error declining friend request:', error);
    return false;
  }
};

// Helper function to check if connection exists
export const checkConnectionExists = async (userId1: string, userId2: string): Promise<boolean> => {
  try {
    const [smallerId, largerId] = userId1 < userId2 ? [userId1, userId2] : [userId2, userId1];

    const { data, error } = await supabase
      .from('connections')
      .select('id')
      .eq('user_id', smallerId)
      .eq('connection_user_id', largerId)
      .eq('status', 'ACCEPTED')  // ✅ FIX: Use uppercase to match connection_status_enum
      .limit(1);

    if (error) {
      console.error('Error checking connection:', error);
      return false;
    }

    return (data && data.length > 0);
  } catch (error) {
    console.error('Unexpected error checking connection:', error);
    return false;
  }
};

// Helper function to check if friend request exists
export const checkFriendRequestExists = async (userId1: string, userId2: string): Promise<boolean> => {
  try {
    // V163.4: Connection requests now loaded via NetworkDataContext
    console.warn('⚠️ V163.4: checkFriendRequestExists() - Use NetworkDataContext for connection requests');

    // Components should use useConnectionRequests() hook and filter locally
    // This prevents duplicate queries to connection_requests table
    return false; // Neutralized - use context hook for checking

    if (error) {
      console.error('Error checking friend request:', error);
      return false;
    }

    return (data && data.length > 0);
  } catch (error) {
    console.error('Unexpected error checking friend request:', error);
    return false;
  }
};

/**
 * 🚨 V182.1: Block a user using connections table with BLOCKED status
 * Works for all connections (1st, 2nd, 3rd degree)
 * Also disconnects if there's an ACCEPTED connection
 * @param userId - The ID of the user initiating the block
 * @param targetUserId - The ID of the user to block
 * @returns Promise with success status and error message if any
 */
export const blockUser = async (userId: string, targetUserId: string): Promise<{ success: boolean; error?: string }> => {
  console.log('🚨 V182.1: Blocking user:', { userId, targetUserId });

  try {
    // ✅ SECURITY: Prevent self-blocking
    if (userId === targetUserId) {
      console.error('❌ V182.1: Cannot block yourself');
      return { success: false, error: 'Cannot block yourself' };
    }

    // Step 1: Find any existing connection between these users
    let connectionId: string | null = null;
    let connectionStatus: string | null = null;
    try {
      const { data: connection } = await withConnectionsAccess(() =>
        supabase
          .from('connections')
          .select('id, status')
          .or(`and(user_id.eq.${userId},connection_user_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},connection_user_id.eq.${userId})`)
          .maybeSingle()
      );

      connectionId = connection?.id || null;
      connectionStatus = connection?.status || null;
      console.log('🚨 V182.1: Found connection:', { connectionId, connectionStatus });

      // Check if already blocked
      if (connectionStatus === 'BLOCKED') {
        console.log('✅ V182.1: User already blocked');
        return { success: true }; // Already blocked, consider it success
      }
    } catch (connectionError) {
      console.warn('⚠️ V182.1: Failed to find connection (non-critical):', connectionError);
    }

    // Step 2: Find and delete all chats between the two users
    try {
      const chatIdsToDelete: string[] = [];

      // Find chats by connection_id (friend-based chats)
      if (connectionId) {
        const { data: connectionChats } = await supabase
          .from('chats')
          .select('id')
          .eq('connection_id', connectionId)
          .eq('chat_type', 'friend');

        if (connectionChats) {
          chatIdsToDelete.push(...connectionChats.map(c => c.id));
        }
      }

      // Find chats by user IDs (legacy system)
      // Sort user IDs for consistent query
      const [user1Id, user2Id] = userId < targetUserId ? [userId, targetUserId] : [targetUserId, userId];

      const { data: legacyChats } = await supabase
        .from('chats')
        .select('id')
        .or(`and(user1_id.eq.${user1Id},user2_id.eq.${user2Id}),and(user1_id.eq.${user2Id},user2_id.eq.${user1Id})`);

      if (legacyChats) {
        chatIdsToDelete.push(...legacyChats.map(c => c.id));
      }

      // Remove duplicates
      const uniqueChatIds = [...new Set(chatIdsToDelete)];

      if (uniqueChatIds.length > 0) {
        console.log(`🗑️ V175.BLOCK: Deleting ${uniqueChatIds.length} chat(s) between users`);

        // Delete all chats (messages will be deleted automatically via CASCADE)
        const { error: deleteChatsError } = await supabase
          .from('chats')
          .delete()
          .in('id', uniqueChatIds);

        if (deleteChatsError) {
          console.warn('⚠️ V175.BLOCK: Failed to delete chats (non-critical):', deleteChatsError);
        } else {
          console.log('✅ V175.BLOCK: Chats deleted successfully');

          // Clear chat cache for both users
          try {
            const { ChatMessageCache } = await import('../ChatMessageCache');
            uniqueChatIds.forEach(chatId => {
              ChatMessageCache.clearChat(chatId, userId);
              ChatMessageCache.clearChat(chatId, targetUserId);
            });
            console.log('✅ V175.BLOCK: Chat cache cleared');
          } catch (cacheError) {
            console.warn('⚠️ V175.BLOCK: Failed to clear chat cache (non-critical):', cacheError);
          }
        }
      } else {
        console.log('ℹ️ V175.BLOCK: No chats found between users');
      }
    } catch (chatError) {
      console.warn('⚠️ V175.BLOCK: Failed to delete chats (non-critical):', chatError);
      // Don't fail the block operation if chat deletion fails
    }

    // Step 3: Update or create BLOCKED connection (CRITICAL - this is how we track blocks)
    try {
      if (connectionId) {
        // Update existing connection to BLOCKED
        console.log('🔒 V175.BLOCK: Updating existing connection to BLOCKED:', connectionId);
        const { error: updateError } = await withConnectionsAccess(() =>
          supabase
            .from('connections')
            .update({
              status: 'BLOCKED',
              updated_at: new Date().toISOString()
            })
            .eq('id', connectionId)
        );

        if (updateError) {
          console.error('❌ V175.BLOCK: Failed to update connection status:', updateError);
          return { success: false, error: 'Failed to update connection status' };
        } else {
          console.log('✅ V175.BLOCK: Connection status updated to BLOCKED');
        }
      } else {
        // No connection exists - create a new BLOCKED connection
        console.log('🔒 V175.BLOCK: Creating new BLOCKED connection between users');
        const { error: insertError } = await withConnectionsAccess(() =>
          supabase
            .from('connections')
            .insert({
              user_id: userId,
              connection_user_id: targetUserId,
              status: 'BLOCKED',
              type: 'CONNECTION', // Default type
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
        );

        if (insertError) {
          console.error('❌ V175.BLOCK: Failed to create BLOCKED connection:', insertError);
          return { success: false, error: 'Failed to create blocked connection' };
        } else {
          console.log('✅ V175.BLOCK: BLOCKED connection created successfully');
        }
      }
    } catch (connectionError) {
      console.error('❌ V175.BLOCK: Exception handling connection status:', connectionError);
      return { success: false, error: 'Failed to block user' };
    }

    // CURSOR PATCH: Server RPC recomputes both users after block
    try {
      await supabase.rpc('recompute_trust_for_users', { user_ids: [userId, targetUserId] });
    } catch (e) {
      console.warn('[TrustScore] recompute RPC failed', e);
    }

    // V175.BLOCK: Log successful block registration for mutual invisibility
    console.log('✅ V175.BLOCK: Block operation completed successfully', {
      userId,
      blockedUserId: targetUserId,
      connectionUpdated: !!connectionId,
      mutualInvisibility: 'Both users will stop seeing each other'
    });

    return { success: true };

  } catch (err) {
    console.error('❌ BLOCK: Service layer error:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Block operation failed'
    };
  }
};

/**
 * V175.BLOCK: Check if a user is blocked (BIDIRECTIONAL)
 * Returns true if there's a BLOCKED connection between users
 * This ensures mutual invisibility - if either side blocks, both sides stop seeing each other
 *
 * @param userId - The ID of the user checking
 * @param targetUserId - The ID of the user to check
 * @returns Promise<boolean> - true if blocked in either direction
 */
export const isUserBlocked = async (userId: string, targetUserId: string): Promise<boolean> => {
  try {
    // V175.BLOCK: Check connections table for BLOCKED status
    const { data, error } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('id')
        .or(`and(user_id.eq.${userId},connection_user_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},connection_user_id.eq.${userId})`)
        .eq('status', 'BLOCKED')
        .maybeSingle()
    );

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('❌ V175.BLOCK: Error checking if user is blocked:', error);
      return false;
    }

    const isBlocked = !!data;
    if (isBlocked) {
      console.log('🔒 V175.BLOCK: Block detected (bidirectional):', { userId, targetUserId });
    }

    return isBlocked;
  } catch (error) {
    console.error('❌ V175.BLOCK: Exception checking if user is blocked:', error);
    return false;
  }
};

/**
 * V175.BLOCK: Get all blocked user IDs for a user (BIDIRECTIONAL)
 * Returns users that have BLOCKED connections with the user
 * This ensures mutual invisibility - if either side blocks, both sides stop seeing each other
 *
 * @param userId - The ID of the user
 * @returns Promise<string[]> - Array of blocked user IDs (bidirectional)
 */
export const getBlockedUserIds = async (userId: string): Promise<string[]> => {
  try {
    console.log('🔒 V175.BLOCK: Fetching blocked users (bidirectional) for:', userId);

    // V175.BLOCK: Fetch all BLOCKED connections involving this user
    const { data: blockedConnections, error } = await withConnectionsAccess(() =>
      supabase
        .from('connections')
        .select('user_id, connection_user_id')
        .or(`user_id.eq.${userId},connection_user_id.eq.${userId}`)
        .eq('status', 'BLOCKED')
    );

    if (error) {
      console.error('❌ V175.BLOCK: Error fetching blocked connections:', error);
      return [];
    }

    // V175.BLOCK: Build set of user IDs that should be hidden
    const blockedIds = new Set<string>();

    blockedConnections?.forEach(conn => {
      // Add the other user in the connection
      if (conn.user_id === userId) {
        blockedIds.add(conn.connection_user_id);
      } else {
        blockedIds.add(conn.user_id);
      }
    });

    const result = Array.from(blockedIds);
    console.log('✅ V175.BLOCK: Loaded', result.length, 'blocked users (bidirectional):', result);

    return result;
  } catch (error) {
    console.error('❌ V175.BLOCK: Exception fetching blocked users:', error);
    return [];
  }
};

/**
 * 🚨 V182.1: Report a user - submits a report for moderation review
 * Stores reports in dedicated user_reports table (NOT connections table)
 * This avoids supabaseGuard violations and provides proper report structure
 *
 * @param reporterId - The ID of the user making the report
 * @param reportedUserId - The ID of the user being reported
 * @param reasons - Array of report reasons (e.g., ['fake', 'harassment'])
 * @param note - Optional additional context from reporter
 * @returns Promise with success status and error message if any
 */
export const reportUser = async (
  reporterId: string,
  reportedUserId: string,
  reasons: string[],
  note?: string
): Promise<{ success: boolean; error?: string }> => {
  console.log('🚨 V182.1: reportUser called', { reporterId, reportedUserId, reasons, note });

  try {
    // ✅ SECURITY: Prevent self-reporting
    if (reporterId === reportedUserId) {
      console.error('❌ V182.1: Cannot report yourself');
      return { success: false, error: 'Cannot report yourself' };
    }

    // ✅ VALIDATION: Ensure at least one reason is provided
    if (!reasons || reasons.length === 0) {
      console.error('❌ V182.1: No reasons provided');
      return { success: false, error: 'At least one reason is required' };
    }

    // 🚨 V182.1: Insert into user_reports table (NOT connections)
    // This table is NOT guarded by supabaseGuard, so no violations
    const { error } = await supabase
      .from('user_reports')
      .insert({
        reporter_id: reporterId,
        reported_user_id: reportedUserId,
        reasons,
        note: note || null,
        status: 'pending',
      });

    if (error) {
      console.error('❌ V182.1: Failed to insert user_reports row', error);
      return { success: false, error: error.message || 'Failed to submit report' };
    }

    console.log('✅ V182.1: Report stored in user_reports table');
    return { success: true };

  } catch (err) {
    console.error('❌ V182.1: Exception in reportUser', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Report operation failed'
    };
  }
};

// Add friendRequestService export for compatibility
export const friendRequestService = {
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  checkConnectionExists,
  checkFriendRequestExists,
  calculateUserTrustScore,
  getUserConnections,
  testConnectionSystem,
  debugConnections,
  createTestConnectionRequest,
  disconnectUser,
  disconnectConnection,
  blockUser,
  isUserBlocked,
  getBlockedUserIds,
  reportUser,
};

